import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Star, ArrowLeft, Globe, Calendar, ExternalLink, FileText, Share2, Mail, Lock, Copy, CheckCircle, ChevronRight, Gift } from 'lucide-react';
import { getOffer, getOfferRatings, createRating, getNearbyRestaurants, getSimilarCuisineRestaurants, getSpecialEvents } from '../services/api';
import { useAuth } from '../context/AuthContext';
import StarRating from '../components/StarRating';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DealDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [offer, setOffer] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [nearbyRestaurants, setNearbyRestaurants] = useState([]);
  const [similarRestaurants, setSimilarRestaurants] = useState([]);
  const [specialEvents, setSpecialEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [userRating, setUserRating] = useState(0);
  const [userComment, setUserComment] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [sharing, setSharing] = useState(false);
  const [voucherRevealed, setVoucherRevealed] = useState(false);
  const [copiedVoucher, setCopiedVoucher] = useState(false);

  const defaultImages = [
    'https://images.pexels.com/photos/6205791/pexels-photo-6205791.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [offerRes, ratingsRes, eventsRes] = await Promise.all([
          getOffer(id),
          getOfferRatings(id),
          getSpecialEvents()
        ]);
        setOffer(offerRes.data);
        setRatings(ratingsRes.data);
        setSpecialEvents(eventsRes.data);

        // Fetch nearby and similar restaurants if we have restaurant_id
        if (offerRes.data.restaurant_id) {
          try {
            const [nearbyRes, similarRes] = await Promise.all([
              getNearbyRestaurants(offerRes.data.restaurant_id, 500),
              getSimilarCuisineRestaurants(offerRes.data.restaurant_id, 1)
            ]);
            setNearbyRestaurants(nearbyRes.data);
            setSimilarRestaurants(similarRes.data);
          } catch (e) {
            console.log('Could not fetch nearby/similar restaurants');
          }
        }
      } catch (error) {
        console.error('Error fetching offer:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleSubmitRating = async () => {
    if (!user) {
      toast.error('Please log in to leave a review');
      return;
    }
    if (userRating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setSubmitting(true);
    try {
      await createRating({ offer_id: id, rating: userRating, comment: userComment || null });
      toast.success('Review submitted!');
      const ratingsRes = await getOfferRatings(id);
      setRatings(ratingsRes.data);
      const offerRes = await getOffer(id);
      setOffer(offerRes.data);
      setUserRating(0);
      setUserComment('');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to submit review';
      toast.error(typeof msg === 'string' ? msg : 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const handleShareDeal = async () => {
    if (!user) {
      toast.error('Please log in to share deals');
      return;
    }
    if (!shareEmail) {
      toast.error('Please enter an email address');
      return;
    }
    setSharing(true);
    try {
      await axios.post(`${API_URL}/api/deals/share`, {
        offer_id: id,
        recipient_email: shareEmail,
        sender_name: user.name,
        message: shareMessage
      }, { withCredentials: true });
      toast.success('Deal shared successfully!');
      setShowShareModal(false);
      setShareEmail('');
      setShareMessage('');
    } catch (error) {
      toast.error('Failed to share deal');
    } finally {
      setSharing(false);
    }
  };

  const handleEmailToSelf = async () => {
    if (!user) {
      toast.error('Please log in to email this deal');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/deals/email-to-self`, { offer_id: id }, { withCredentials: true });
      toast.success(`Deal details sent to ${user.email}!`);
    } catch (error) {
      toast.error('Failed to send email');
    }
  };

  const handleCopyVoucher = () => {
    if (offer?.voucher_code) {
      navigator.clipboard.writeText(offer.voucher_code);
      setCopiedVoucher(true);
      toast.success('Voucher code copied!');
      setTimeout(() => setCopiedVoucher(false), 3000);
    }
  };

  const generateGoogleMapsLink = (address) => {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  const dayLabels = {
    monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
    thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday'
  };

  const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  if (loading) {
    return (
      <div className="min-h-screen bg-white py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="animate-pulse">
            <div className="h-6 bg-[#F5F5F5] rounded w-24 mb-8" />
            <div className="h-72 bg-[#F5F5F5] rounded-xl mb-6" />
            <div className="h-8 bg-[#F5F5F5] rounded w-3/4 mb-3" />
            <div className="h-5 bg-[#F5F5F5] rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="min-h-screen bg-white py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-xl font-bold text-[#242E30] mb-4">Deal Not Found</h1>
          <Link to="/deals" className="text-[#7B2CBF] hover:underline">Back to Deals</Link>
        </div>
      </div>
    );
  }

  const imageUrl = offer.image_url || offer.restaurant_image || defaultImages[0];
  const openingTimes = offer.restaurant_opening_times || {};

  return (
    <div className="min-h-screen bg-white" data-testid="deal-detail-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Back Link */}
        <Link 
          to="/deals" 
          className="inline-flex items-center gap-2 text-[#595959] hover:text-[#7B2CBF] transition-colors mb-6 text-sm font-medium"
          data-testid="back-to-deals"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to deals
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Hero Image */}
            <div className="relative h-64 sm:h-80 rounded-xl overflow-hidden mb-6">
              <img src={imageUrl} alt={offer.title} className="w-full h-full object-cover" />
              <div className="absolute top-4 left-4 bg-[#7B2CBF] text-white px-4 py-2 rounded-lg font-bold">
                {offer.discount_text}
              </div>
              {offer.avg_rating > 0 && (
                <div className="absolute top-4 right-4 bg-white text-[#242E30] flex items-center gap-1 px-3 py-2 rounded-lg font-semibold shadow-sm">
                  <Star className="h-4 w-4 fill-[#FFB800] text-[#FFB800]" />
                  {offer.avg_rating.toFixed(1)}
                  <span className="text-[#595959] text-sm ml-1">({offer.rating_count})</span>
                </div>
              )}
            </div>

            {/* Title & Description */}
            <h1 className="text-2xl sm:text-3xl font-bold text-[#242E30] mb-3">{offer.title}</h1>
            <p className="text-[#595959] mb-6">{offer.description}</p>

            {/* Voucher Code Section - show if user is logged in and code exists, or prompt to login */}
            {(offer.voucher_code || offer.has_voucher_code) && (
              <div className="bg-[#7B2CBF]/5 border border-[#7B2CBF]/20 rounded-xl p-5 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-[#242E30] mb-1">Voucher Code</h3>
                    {user && user.id ? (
                      offer.voucher_code ? (
                        voucherRevealed ? (
                          <div className="flex items-center gap-3">
                            <code className="bg-white px-4 py-2 rounded-lg text-lg font-mono font-bold text-[#7B2CBF] border border-[#7B2CBF]/30">
                              {offer.voucher_code}
                            </code>
                            <button
                              onClick={handleCopyVoucher}
                              className="flex items-center gap-1 text-sm text-[#7B2CBF] hover:underline"
                            >
                              {copiedVoucher ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                              {copiedVoucher ? 'Copied!' : 'Copy'}
                            </button>
                          </div>
                        ) : (
                          <Button
                            onClick={() => setVoucherRevealed(true)}
                            className="bg-[#7B2CBF] hover:bg-[#5A189A] text-white"
                            data-testid="reveal-voucher-btn"
                          >
                            Reveal Code
                          </Button>
                        )
                      ) : (
                        <p className="text-sm text-[#595959]">Loading voucher...</p>
                      )
                    ) : (
                      <div className="flex items-center gap-2 text-[#595959]">
                        <Lock className="h-4 w-4" />
                        <span className="text-sm">
                          <Link to="/login" className="text-[#7B2CBF] hover:underline font-medium">Log in</Link> or{' '}
                          <Link to="/register" className="text-[#7B2CBF] hover:underline font-medium">sign up</Link> to see the voucher code
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Days */}
            <div className="mb-6">
              <h3 className="text-xs font-semibold text-[#595959] uppercase tracking-wider mb-3">Available days</h3>
              <div className="flex flex-wrap gap-2">
                {(offer.days || []).map((day) => (
                  <span key={day} className="bg-[#7B2CBF]/10 text-[#7B2CBF] font-medium px-3 py-1.5 rounded-lg text-sm">
                    {dayLabels[day] || day}
                  </span>
                ))}
              </div>
            </div>

            {/* Share Actions */}
            <div className="flex flex-wrap gap-3 mb-6">
              <Button
                variant="outline"
                onClick={() => setShowShareModal(true)}
                className="flex items-center gap-2"
                data-testid="share-deal-btn"
              >
                <Share2 className="h-4 w-4" />
                Send to Friend
              </Button>
              {user && user.id && (
                <Button
                  variant="outline"
                  onClick={handleEmailToSelf}
                  className="flex items-center gap-2"
                  data-testid="email-self-btn"
                >
                  <Mail className="h-4 w-4" />
                  Email to Myself
                </Button>
              )}
            </div>

            {/* Terms & Conditions */}
            {offer.terms_conditions && (
              <div className="bg-[#F5F5F5] rounded-xl p-5 mb-6">
                <h3 className="font-semibold text-[#242E30] mb-2 text-sm">Terms & Conditions</h3>
                <p className="text-[#595959] text-sm whitespace-pre-line">{offer.terms_conditions}</p>
              </div>
            )}

            {/* Ratings Section */}
            <div className="bg-[#F5F5F5] rounded-xl p-6">
              <h3 className="text-lg font-bold text-[#242E30] mb-6">Reviews ({ratings.length})</h3>

              {user && user.role === 'user' && (
                <div className="mb-6 pb-6 border-b border-[#E8E8E8]">
                  <h4 className="font-medium text-[#242E30] text-sm mb-3">Leave a review</h4>
                  <div className="mb-3">
                    <StarRating rating={userRating} size="lg" interactive onRate={setUserRating} />
                  </div>
                  <Textarea
                    placeholder="Share your experience (optional)"
                    value={userComment}
                    onChange={(e) => setUserComment(e.target.value)}
                    className="mb-3 bg-white"
                    data-testid="rating-comment"
                  />
                  <Button
                    onClick={handleSubmitRating}
                    disabled={submitting || userRating === 0}
                    className="bg-[#7B2CBF] hover:bg-[#5A189A] text-white"
                    data-testid="submit-rating-btn"
                  >
                    {submitting ? 'Submitting...' : 'Submit review'}
                  </Button>
                </div>
              )}

              {ratings.length > 0 ? (
                <div className="space-y-4">
                  {ratings.map((rating) => (
                    <div key={rating.id} className="bg-white rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-[#242E30] text-sm">{rating.user_name}</span>
                        <StarRating rating={rating.rating} size="sm" />
                      </div>
                      {rating.comment && <p className="text-[#595959] text-sm">{rating.comment}</p>}
                      <span className="text-xs text-[#595959]/60 mt-2 block">
                        {new Date(rating.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#595959] text-center py-4 text-sm">No reviews yet. Be the first!</p>
              )}
            </div>

            {/* Nearby Restaurants - within 500 yards */}
            {nearbyRestaurants.length > 0 && (
              <div className="bg-[#F5F5F5] rounded-xl p-6 mt-6">
                <h3 className="text-lg font-bold text-[#242E30] mb-4">
                  Restaurants within 500 yards
                </h3>
                <div className="space-y-3">
                  {nearbyRestaurants.map((r) => (
                    <Link
                      key={r.id}
                      to={`/restaurant/${r.id}`}
                      className="flex items-center gap-3 p-3 bg-white rounded-xl hover:shadow-md transition-all"
                    >
                      <img 
                        src={r.image_url || defaultImages[0]} 
                        alt={r.name}
                        className="w-14 h-14 object-cover rounded-lg"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-[#242E30] text-sm truncate">{r.name}</h4>
                        {r.cuisine_type && <p className="text-xs text-[#7B2CBF]">{r.cuisine_type}</p>}
                        {r.distance_yards && (
                          <p className="text-xs text-[#595959]">{r.distance_yards} yards away</p>
                        )}
                      </div>
                      <ChevronRight className="h-4 w-4 text-[#595959] shrink-0" />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Similar Cuisine Restaurants - within 1 mile */}
            {similarRestaurants.length > 0 && offer.cuisine_type && (
              <div className="bg-[#F5F5F5] rounded-xl p-6 mt-6">
                <h3 className="text-lg font-bold text-[#242E30] mb-4">
                  Other {offer.cuisine_type} restaurants within 1 mile
                </h3>
                <div className="space-y-3">
                  {similarRestaurants.map((r) => (
                    <Link
                      key={r.id}
                      to={`/restaurant/${r.id}`}
                      className="flex items-center gap-3 p-3 bg-white rounded-xl hover:shadow-md transition-all"
                    >
                      <img 
                        src={r.image_url || defaultImages[0]} 
                        alt={r.name}
                        className="w-14 h-14 object-cover rounded-lg"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-[#242E30] text-sm truncate">{r.name}</h4>
                        {r.cuisine_type && <p className="text-xs text-[#7B2CBF]">{r.cuisine_type}</p>}
                        {r.distance_miles && (
                          <p className="text-xs text-[#595959]">{r.distance_miles} miles away</p>
                        )}
                      </div>
                      <ChevronRight className="h-4 w-4 text-[#595959] shrink-0" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Restaurant Info */}
          <div className="lg:col-span-1">
            {/* Special Events Banner */}
            {specialEvents.length > 0 && (
              <div className="bg-gradient-to-r from-[#7B2CBF] to-[#9D4EDD] rounded-xl p-5 mb-6 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="h-5 w-5" />
                  <h3 className="font-bold">Special Dates Coming Up!</h3>
                </div>
                <div className="space-y-2">
                  {specialEvents.slice(0, 3).map((event, idx) => (
                    <div key={idx} className="text-sm">
                      <span className="font-medium">{event.name}</span>
                      <span className="text-white/70 ml-2">
                        {event.days_until === 0 ? 'Today!' : 
                         event.days_until === 1 ? 'Tomorrow!' : 
                         `in ${event.days_until} days`}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-white/70 mt-3">
                  Ask this restaurant about special menus!
                </p>
              </div>
            )}

            <div className="bg-[#F5F5F5] rounded-xl p-5 sticky top-24">
              {/* Logo */}
              {offer.restaurant_logo && (
                <div className="w-20 h-20 mb-4 mx-auto">
                  <img 
                    src={`${API_URL}/api/files/${offer.restaurant_logo}`} 
                    alt={offer.restaurant_name}
                    className="w-full h-full object-contain rounded-lg bg-white p-2"
                  />
                </div>
              )}

              <h3 className="font-bold text-[#242E30] text-lg mb-1 text-center">{offer.restaurant_name}</h3>
              {offer.cuisine_type && (
                <p className="text-[#7B2CBF] text-sm font-medium text-center mb-2">{offer.cuisine_type}</p>
              )}

              {/* Link to restaurant detail page */}
              {offer.restaurant_id && (
                <Link
                  to={`/restaurant/${offer.restaurant_id}`}
                  className="flex items-center justify-center gap-1 text-sm text-[#7B2CBF] hover:underline mb-4"
                >
                  View all deals & info
                  <ChevronRight className="h-4 w-4" />
                </Link>
              )}

              <div className="space-y-3">
                {/* Address with Google Maps link */}
                {offer.restaurant_address && (
                  <a 
                    href={generateGoogleMapsLink(offer.restaurant_address)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-2 text-[#595959] hover:text-[#7B2CBF] transition-colors group"
                  >
                    <MapPin className="h-4 w-4 text-[#7B2CBF] shrink-0 mt-0.5" />
                    <span className="text-sm group-hover:underline">{offer.restaurant_address}</span>
                  </a>
                )}

                {/* Phone with tel link */}
                {offer.restaurant_phone && (
                  <a 
                    href={`tel:${offer.restaurant_phone}`}
                    className="flex items-center gap-2 text-[#595959] hover:text-[#7B2CBF] transition-colors"
                  >
                    <Phone className="h-4 w-4 text-[#7B2CBF] shrink-0" />
                    <span className="text-sm hover:underline">{offer.restaurant_phone}</span>
                  </a>
                )}

                {/* Website */}
                {offer.restaurant_website && (
                  <a 
                    href={offer.restaurant_website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-[#595959] hover:text-[#7B2CBF] transition-colors"
                  >
                    <Globe className="h-4 w-4 text-[#7B2CBF] shrink-0" />
                    <span className="text-sm hover:underline">Visit Website</span>
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}

                {/* Booking */}
                {offer.restaurant_booking && (
                  <a 
                    href={offer.restaurant_booking}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-[#7B2CBF] text-white hover:bg-[#5A189A] rounded-lg px-4 py-2.5 font-medium transition-colors mt-4"
                  >
                    <Calendar className="h-4 w-4" />
                    Book a Table
                  </a>
                )}
              </div>

              {/* Opening Times */}
              {Object.keys(openingTimes).length > 0 && (
                <div className="mt-6 pt-4 border-t border-[#E8E8E8]">
                  <h4 className="font-semibold text-[#242E30] text-sm mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-[#7B2CBF]" />
                    Opening Times
                  </h4>
                  <div className="space-y-1">
                    {dayOrder.map((day) => (
                      openingTimes[day] && (
                        <div key={day} className="flex justify-between text-sm">
                          <span className="text-[#595959] capitalize">{day}</span>
                          <span className="text-[#242E30] font-medium">{openingTimes[day]}</span>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}

              {/* Menu PDF Download */}
              {offer.restaurant_menu_pdf && (
                <a 
                  href={`${API_URL}/api/files/${offer.restaurant_menu_pdf}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-white border border-[#E8E8E8] text-[#242E30] hover:border-[#7B2CBF] hover:text-[#7B2CBF] rounded-lg px-4 py-2.5 font-medium transition-colors mt-4"
                >
                  <FileText className="h-4 w-4" />
                  Download Menu (PDF)
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Share this deal</DialogTitle>
            <DialogDescription>Send this deal to a friend via email</DialogDescription>
          </DialogHeader>
          {user && user.id ? (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#242E30]">Friend's email</label>
                <Input
                  type="email"
                  placeholder="friend@example.com"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  className="mt-1"
                  data-testid="share-email-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#242E30]">Message (optional)</label>
                <Textarea
                  placeholder="Check out this deal!"
                  value={shareMessage}
                  onChange={(e) => setShareMessage(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowShareModal(false)}>Cancel</Button>
                <Button 
                  onClick={handleShareDeal} 
                  disabled={sharing}
                  className="bg-[#7B2CBF] hover:bg-[#5A189A] text-white"
                  data-testid="share-submit-btn"
                >
                  {sharing ? 'Sending...' : 'Send'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-[#595959] mb-4">Please log in to share deals</p>
              <Link to="/login" className="text-[#7B2CBF] hover:underline font-medium">Log in</Link>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DealDetailPage;
